/**
 * @description Hook para buscar e processar dados de profissionais
 * @module usePractitionerCards
 * @since Novembro 2024
 */

import { useState, useEffect, useCallback } from "react";
import { GetPractitionerRole } from "@/services/fhir/practitionerrole/GetPractitionerRole";
import { GetSchedule } from "@/services/fhir/schedule/GetSchedule";

/**
 * @interface Practitioner
 * @description Estrutura de dados do profissional processado
 */
export interface Practitioner {
  id: string;
  name: string;
  specialty: string;
  description?: string;
  phone?: string;
  instagram?: string;
  imageUrl?: string;
  /** Indica se o profissional permite apresentação de horários no perfil público */
  schedulePresentation?: boolean;
}

/**
 * @interface UsePractitionerCardsProps
 * @description Props do hook
 */
interface UsePractitionerCardsProps {
  organization?: string;
  /** Filtro de localização (Location ID) */
  location?: string;
  /**
   * Filtro de modelo de atendimento.
   * Valores: "virtualService" | "chatService" | "domicilioService" | "physicalLocation"
   */
  serviceModel?: string;
  /** Filtro de especialidade (HealthcareService display name) */
  specialty?: string;
  /** Filtro de estado (UF) */
  state?: string;
}

/**
 * @description Verifica se o Practitioner tem a extensão profile-exposure habilitada
 */
function hasProfileExposureEnabled(practitioner: any): boolean {
  const profileExposureExtension = practitioner?.extension?.find(
    (ext: any) =>
      ext.url ===
      "http://fluxmed.com.br/fhir/StructureDefinition/practitioner-profile-exposure",
  );
  return profileExposureExtension?.valueBoolean === true;
}

/**
 * @description Verifica se o Practitioner tem a extensão schedule-presentation habilitada
 * Por padrão, se a extensão não existir, retorna true (horários serão exibidos)
 * Retorna true se habilitado, false se desabilitado explicitamente
 */
function hasSchedulePresentationEnabled(practitioner: any): boolean {
  const schedulePresentationExtension = practitioner?.extension?.find(
    (ext: any) =>
      ext.url ===
      "http://fluxmed.com.br/fhir/StructureDefinition/practitioner-schedule-presentation",
  );
  // Se a extensão não existe, retorna true (padrão: exibir horários)
  if (!schedulePresentationExtension) return true;
  // Se existe, retorna o valor booleano (true = exibir horários, false = apenas dias)
  return schedulePresentationExtension?.valueBoolean !== false;
}

/**
 * @description Verifica se o PractitionerRole possui o código RL_PRACTITIONER
 */
function hasPractitionerRole(practitionerRole: any): boolean {
  return practitionerRole?.code?.some((codeItem: any) =>
    codeItem.coding?.some(
      (coding: any) =>
        coding.system === "practitioner" && coding.code === "RL_PRACTITIONER",
    ),
  );
}

/**
 * @description Extrai a descrição do Practitioner a partir das extensions
 */
function extractDescription(practitioner: any): string | undefined {
  const descriptionExtension = practitioner?.extension?.find(
    (ext: any) =>
      ext.url ===
      "http://fluxmed.com.br/fhir/StructureDefinition/practitioner-description",
  );
  return descriptionExtension?.valueString;
}

/**
 * @description Extrai o telefone do Practitioner a partir do telecom
 */
function extractPhone(practitioner: any): string | undefined {
  const phoneTelecom = practitioner?.telecom?.find(
    (tel: any) => tel.system === "phone",
  );
  return phoneTelecom?.value;
}

/**
 * @description Extrai o Instagram do Practitioner a partir dos identifiers
 */
function extractInstagram(practitioner: any): string | undefined {
  const instagramIdentifier = practitioner?.identifier?.find(
    (id: any) => id.system === "https://instagram.com",
  );
  return instagramIdentifier?.value
    ? `@${instagramIdentifier.value}`
    : undefined;
}

/**
 * @description Extrai a foto do Practitioner (base64 ou URL)
 */
function extractPhoto(practitioner: any): string | undefined {
  const photo = practitioner?.photo?.[0];
  if (!photo) return undefined;

  if (photo.data) {
    const contentType = photo.contentType || "image/png";
    return `data:${contentType};base64,${photo.data}`;
  }

  return photo.url;
}

/**
 * @description Extrai as especialidades do PractitionerRole
 */
function extractSpecialties(practitionerRole: any): string {
  const specialties = practitionerRole?.specialty
    ?.map((spec: any) => spec.coding?.[0]?.display)
    .filter(Boolean);

  return specialties?.join(", ") || "Especialidade não informada";
}

/**
 * @description Extrai o nome do Practitioner
 */
function extractName(practitioner: any): string {
  return (
    practitioner?.name?.[0]?.text ||
    practitioner?.name?.[0]?.given?.join(" ") +
      " " +
      practitioner?.name?.[0]?.family ||
    "Nome não informado"
  );
}

/**
 * @description Processa o Bundle FHIR e retorna lista de profissionais
 */
function processPractitionerBundle(bundle: any): Practitioner[] {
  if (!bundle?.entry?.length) return [];

  const practitioners: Map<string, any> = new Map();
  const practitionerRoles: Map<string, any> = new Map();

  // Separa os recursos por tipo - filtra apenas PractitionerRoles com RL_PRACTITIONER
  bundle.entry.forEach((entry: any) => {
    const resource = entry.resource;
    if (resource.resourceType === "Practitioner") {
      practitioners.set(resource.id, resource);
    } else if (resource.resourceType === "PractitionerRole") {
      // Só inclui se tiver o código RL_PRACTITIONER
      if (hasPractitionerRole(resource)) {
        const practitionerRef =
          resource.practitioner?.reference?.split("/")?.[1];
        if (practitionerRef) {
          practitionerRoles.set(practitionerRef, resource);
        }
      }
    }
  });

  // Combina os dados - filtra apenas profissionais com profile-exposure habilitado
  const result: Practitioner[] = [];

  practitionerRoles.forEach((role, practitionerId) => {
    const practitioner = practitioners.get(practitionerId);
    // Só inclui se o profissional existir e tiver profile-exposure habilitado
    if (practitioner && hasProfileExposureEnabled(practitioner)) {
      result.push({
        id: practitionerId,
        name: extractName(practitioner),
        specialty: extractSpecialties(role),
        description: extractDescription(practitioner),
        phone: extractPhone(practitioner),
        instagram: extractInstagram(practitioner),
        imageUrl: extractPhoto(practitioner),
        // Inclui a configuração de apresentação de horários
        schedulePresentation: hasSchedulePresentationEnabled(practitioner),
      });
    }
  });

  return result;
}

/**
 * @description Verifica se uma Location é de teleconsulta (virtual-service=true)
 */
function isVirtualService(location: fhir5.Location): boolean {
  return !!location?.extension?.find(
    (ext) =>
      ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
      ext.valueBoolean === true,
  );
}

/**
 * @description Lê o canal de atendimento da extension attendance-channel.
 * Valores possíveis: "teleatendimento", "chat", "domicilio".
 */
function getAttendanceChannel(location: fhir5.Location): string | undefined {
  return (location?.extension?.find((ext) =>
    ext.url?.includes("attendance-channel"),
  ) as any)?.valueCode;
}

/**
 * @description Verifica se uma Location corresponde ao serviceModel solicitado.
 * - virtualService:   virtual-service=true e canal NÃO é chat
 * - chatService:      attendance-channel=chat
 * - domicilioService: attendance-channel=domicilio
 * - physicalLocation: sem virtual-service e sem canal especial
 */
function matchesServiceModel(
  location: fhir5.Location,
  serviceModel: string,
): boolean {
  const channel = getAttendanceChannel(location);
  const isVirtual = isVirtualService(location);

  switch (serviceModel) {
    case "virtualService":
      if (channel === "chat") return false;
      return isVirtual;
    case "chatService":
      return channel === "chat";
    case "domicilioService":
      return channel === "domicilio";
    case "physicalLocation":
      // Presencial: sem flag virtual e sem canal especial
      return !isVirtual && channel !== "chat" && channel !== "domicilio";
    default:
      return true;
  }
}

/**
 * @hook usePractitionerCards
 * @description Hook para gerenciar busca e estado dos profissionais
 * @remarks Se houver filtros (location, serviceModel, specialty, state), usa GetSchedule para buscar
 * schedules filtrados e depois busca os practitioners desses schedules.
 * Caso contrário, busca todos os practitioners diretamente via GetPractitionerRole.
 */
export function usePractitionerCards({
  organization,
  location,
  serviceModel,
  specialty,
  state,
}: UsePractitionerCardsProps) {
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPractitioners = useCallback(async () => {
    try {
      // Importante: não limpamos mais todo o localStorage aqui.
      // A página de cards é externa e compartilha o mesmo storage com a
      // aplicação interna; limpar tudo derruba a sessão do usuário logado.
      setLoading(true);
      setError(null);

      // Se houver filtros, usa GetSchedule para buscar schedules filtrados
      const hasFilters = location || serviceModel || specialty || state;

      if (hasFilters) {
        // Prepara parâmetros para GetSchedule
        const scheduleParams: any = {
          includeLocation: true,
          active: true,
          count: 250,
        };

        // Adiciona filtros se fornecidos
        if (organization) {
          scheduleParams.organization = organization;
        }
        if (location) {
          scheduleParams.location = location;
        }
        if (specialty) {
          scheduleParams.specialty = specialty;
        }
        if (state) {
          scheduleParams.state = state;
        }

        // Busca schedules com os filtros
        const scheduleResponse = await GetSchedule(scheduleParams);

        if (!scheduleResponse?.entry) {
          setPractitioners([]);
          return;
        }

        // Filtra schedules e locations
        const schedules = scheduleResponse.entry.filter(
          (entry: any) => entry?.resource?.resourceType === "Schedule",
        );
        const locations = scheduleResponse.entry.filter(
          (entry: any) => entry?.resource?.resourceType === "Location",
        );

        // Filtra locations pelo modelo de atendimento selecionado
        let filteredLocations = locations;
        if (serviceModel) {
          filteredLocations = locations.filter((locEntry: any) => {
            const loc = locEntry?.resource as fhir5.Location;
            if (!loc) return false;
            return matchesServiceModel(loc, serviceModel);
          });
        }

        // Extrai IDs dos practitioners dos schedules filtrados
        const practitionerIds = new Set<string>();
        schedules.forEach((scheduleEntry: any) => {
          const schedule = scheduleEntry?.resource;
          if (!schedule?.actor) return;

          // Encontra Location actor do schedule
          const locationActor = schedule.actor.find((actor: any) =>
            actor.reference?.startsWith("Location/"),
          );
          if (!locationActor) return;

          const locationId = locationActor.reference.split("/")[1];

          // Verifica se a location está na lista de locations filtradas
          const locationExists = filteredLocations.some(
            (locEntry: any) => locEntry?.resource?.id === locationId,
          );

          if (locationExists) {
            // Encontra Practitioner actor do schedule
            const practitionerActor = schedule.actor.find((actor: any) =>
              actor.reference?.startsWith("Practitioner/"),
            );
            if (practitionerActor?.reference) {
              const practitionerId = practitionerActor.reference.split("/")[1];
              practitionerIds.add(practitionerId);
            }
          }
        });

        // Se não encontrou nenhum practitioner, retorna vazio
        if (practitionerIds.size === 0) {
          setPractitioners([]);
          return;
        }

        // Busca PractitionerRoles dos practitioners encontrados
        const practitionerRolePromises = Array.from(practitionerIds).map(
          (practitionerId) =>
            GetPractitionerRole({
              practitioner: `Practitioner/${practitionerId}`,
              organization,
              includePractitioner: true,
              authAplication: true,
              count: "1000",
            }),
        );

        const practitionerRoleResponses = await Promise.all(
          practitionerRolePromises,
        );

        // Combina todos os bundles em um único bundle
        const combinedBundle: any = {
          resourceType: "Bundle",
          entry: [],
        };

        practitionerRoleResponses.forEach((response) => {
          if (response?.entry) {
            combinedBundle.entry.push(...response.entry);
          }
        });

        // Processa o bundle combinado
        const processedPractitioners =
          processPractitionerBundle(combinedBundle);
        setPractitioners(processedPractitioners);
      } else {
        // Sem filtros, busca diretamente via GetPractitionerRole
        const response = await GetPractitionerRole({
          organization,
          includePractitioner: true,
          authAplication: true,
          count: "1000",
        });

        if (response) {
          const processedPractitioners = processPractitionerBundle(response);
          setPractitioners(processedPractitioners);
        }
      }
    } catch (err) {
      console.error("Erro ao buscar profissionais:", err);
      setError(
        err instanceof Error ? err : new Error("Erro ao buscar profissionais"),
      );
    } finally {
      setLoading(false);
    }
  }, [organization, location, serviceModel, specialty, state]);

  useEffect(() => {
    fetchPractitioners();
  }, [fetchPractitioners]);

  return {
    practitioners,
    loading,
    error,
    refetch: fetchPractitioners,
  };
}
